import React from "react";

import { Song } from "../../types/song";
import { getTodaysSolution, validateSongLibrary } from "../../helpers";

import * as Styled from "./index.styled";

type AuthState =
  | {
      loading: true;
      authenticated: false;
      login?: undefined;
    }
  | {
      loading: false;
      authenticated: boolean;
      login?: string;
    };

function emptySong(): Song {
  return {
    artist: "Taylor Swift",
    name: "",
    youtubeId: "",
  };
}

export function AdminPage() {
  const [auth, setAuth] = React.useState<AuthState>({
    loading: true,
    authenticated: false,
  });
  const [library, setLibrary] = React.useState<Song[]>([]);
  const [jsonImport, setJsonImport] = React.useState<string>("");
  const [message, setMessage] = React.useState<string>("");
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  React.useEffect(() => {
    fetch("/api/auth/me")
      .then((response) => response.json())
      .then((data) => {
        setAuth({
          loading: false,
          authenticated: Boolean(data.authenticated),
          login: data.login,
        });
      })
      .catch(() => {
        setAuth({
          loading: false,
          authenticated: false,
        });
      });

    fetch("/api/library")
      .then((response) => response.json())
      .then((data) => {
        const validation = validateSongLibrary(data.songs);

        if (validation.ok) {
          setLibrary(validation.songs);
        }
      })
      .catch(() => {
        setLibrary([]);
      });
  }, []);

  const updateSong = React.useCallback(
    (index: number, field: keyof Song, value: string) => {
      setLibrary((currentLibrary) =>
        currentLibrary.map((song, songIndex) =>
          songIndex === index ? { ...song, [field]: value } : song
        )
      );
    },
    []
  );

  const addSong = React.useCallback(() => {
    setLibrary((currentLibrary) => [...currentLibrary, emptySong()]);
  }, []);

  const removeSong = React.useCallback((index: number) => {
    setLibrary((currentLibrary) =>
      currentLibrary.filter((_, songIndex) => songIndex !== index)
    );
  }, []);

  const importJson = React.useCallback(() => {
    try {
      const validation = validateSongLibrary(JSON.parse(jsonImport));

      if (validation.ok === false) {
        setMessage(validation.error);
        return;
      }

      setLibrary(validation.songs);
      setMessage("JSON 已导入。");
    } catch {
      setMessage("JSON 格式不正确。");
    }
  }, [jsonImport]);

  const saveLibrary = React.useCallback(() => {
    const validation = validateSongLibrary(library);

    if (validation.ok === false) {
      setMessage(validation.error);
      return;
    }

    setIsSaving(true);
    setMessage("正在保存...");

    fetch("/api/admin/library", {
      method: "PUT",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ songs: validation.songs }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to save library");
        }

        return response.json();
      })
      .then(() => {
        setMessage("曲库已保存。");
      })
      .catch(() => {
        setMessage("保存失败，请确认你已登录 GitHub。");
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [library]);

  const logout = React.useCallback(() => {
    fetch("/api/auth/logout", {
      method: "POST",
      credentials: "same-origin",
    }).finally(() => {
      window.location.href = "/admin";
    });
  }, []);

  const todaySong =
    library.length > 0 ? getTodaysSolution(library).name : "暂无歌曲";

  if (auth.loading) {
    return (
      <Styled.Page>
        <Styled.Title>曲库管理</Styled.Title>
        <Styled.Message>加载中...</Styled.Message>
      </Styled.Page>
    );
  }

  if (!auth.authenticated) {
    return (
      <Styled.Page>
        <Styled.Header>
          <div>
            <Styled.Title>曲库管理</Styled.Title>
            <Styled.Description>
              使用 GitHub 登录后可以维护所有访客共用的霉霉曲库。
            </Styled.Description>
          </div>
        </Styled.Header>
        <Styled.LoginLink href="/api/auth/github/start">
          使用 GitHub 登录
        </Styled.LoginLink>
      </Styled.Page>
    );
  }

  return (
    <Styled.Page>
      <Styled.Header>
        <div>
          <Styled.Title>曲库管理</Styled.Title>
          <Styled.Description>
            已登录：{auth.login}。保存后，所有访客都会使用这套曲库。
          </Styled.Description>
        </div>
        <Styled.Button onClick={logout}>退出登录</Styled.Button>
      </Styled.Header>

      <Styled.Toolbar>
        <Styled.Today>今日歌曲：{todaySong}</Styled.Today>
        <Styled.Button onClick={addSong}>新增歌曲</Styled.Button>
        <Styled.SaveButton disabled={isSaving} onClick={saveLibrary}>
          保存曲库
        </Styled.SaveButton>
      </Styled.Toolbar>

      <Styled.TableWrapper>
        <Styled.Table>
          <thead>
            <tr>
              <th>歌手</th>
              <th>歌名</th>
              <th>YouTube ID</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {library.map((song, index) => (
              <tr key={`${song.youtubeId}-${index}`}>
                <td>
                  <Styled.Input
                    aria-label={`第 ${index + 1} 首歌的歌手`}
                    value={song.artist}
                    onChange={(event) =>
                      updateSong(index, "artist", event.currentTarget.value)
                    }
                  />
                </td>
                <td>
                  <Styled.Input
                    aria-label={`第 ${index + 1} 首歌的歌名`}
                    value={song.name}
                    onChange={(event) =>
                      updateSong(index, "name", event.currentTarget.value)
                    }
                  />
                </td>
                <td>
                  <Styled.Input
                    aria-label={`第 ${index + 1} 首歌的 YouTube ID`}
                    value={song.youtubeId}
                    onChange={(event) =>
                      updateSong(index, "youtubeId", event.currentTarget.value)
                    }
                  />
                </td>
                <td>
                  <Styled.DangerButton onClick={() => removeSong(index)}>
                    删除
                  </Styled.DangerButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Styled.Table>
      </Styled.TableWrapper>

      <Styled.Section>
        <Styled.Label htmlFor="library-json">批量导入 JSON</Styled.Label>
        <Styled.Textarea
          id="library-json"
          value={jsonImport}
          onChange={(event) => setJsonImport(event.currentTarget.value)}
          placeholder='[{"artist":"Taylor Swift","name":"The Archer","youtubeId":"8KpKc3C9V3w"}]'
        />
        <Styled.Toolbar>
          <Styled.Button onClick={importJson}>导入 JSON</Styled.Button>
        </Styled.Toolbar>
        <Styled.Message>{message}</Styled.Message>
      </Styled.Section>
    </Styled.Page>
  );
}
